import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ComparePage.css";
import {
    FLAG_TRANSLATIONS,
    notableEffects,
    categoryTitles,
    INGREDIENTS_EXPLANATIONS,
    ALL_FLAGS,
    getSafetyClass,
    scalpTypeNames,
    hairTypeNames,
    OVERALL_TO_STATUS,
    getStatusIcon,
    getScoreStyle,
    skinTypeNames,
    isSkinCategory,
    skinNotableEffects,
} from "../constants";
import { useCompare } from "../CompareContext";
import { getProductImage } from "../constants";
import products from "../hair_products.json";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";

function allHaveSameFlag(compareList, flag) {
    if (compareList.length < 2) return true;
    const firstHas = !!compareList[0].flags?.[flag]?.length;
    return compareList.every((p) => !!p.flags?.[flag]?.length === firstHas);
}

function collectAllEffects(compareList) {
    const all = new Set();
    compareList.forEach((p) => {
        Object.keys(p.notable_effects || {}).forEach((e) => all.add(e));
    });
    return [...all];
}

function allHaveSameEffect(compareList, effectKey) {
    if (compareList.length < 2) return true;
    const firstHas = !!compareList[0].notable_effects?.[effectKey];
    return compareList.every((p) => !!p.notable_effects?.[effectKey] === firstHas);
}

function collectAllRisks(compareList) {
    const all = new Set();
    compareList.forEach((p) => {
        Object.keys(p.chemical_risks || {}).forEach((r) => all.add(r));
    });
    return [...all];
}

function allHaveSameRisk(compareList, risk) {
    if (compareList.length < 2) return true;
    const firstHas = !!compareList[0].chemical_risks?.[risk];
    return compareList.every((p) => !!p.chemical_risks?.[risk] === firstHas);
}

function collectAllHairTypes(compareList) {
    const all = new Set();
    compareList.forEach((p) => {
        Object.keys(p.hair_scalp_analysis?.hair || {}).forEach((ht) => all.add(ht));
    });
    return [...all];
}

function allHaveSameHairType(compareList, hairKey) {
    if (compareList.length < 2) return true;
    const firstOverall = compareList[0].hair_scalp_analysis?.hair?.[hairKey]?.overall || null;
    return compareList.every(
        (product) => product.hair_scalp_analysis?.hair?.[hairKey]?.overall === firstOverall
    );
}

function collectAllScalpTypes(compareList) {
    const all = new Set();
    compareList.forEach((p) => {
        Object.keys(p.hair_scalp_analysis?.scalp || {}).forEach((st) => all.add(st));
    });
    return [...all];
}

function allHaveSameScalpType(compareList, scalpKey) {
    if (compareList.length < 2) return true;
    const firstOverall = compareList[0].hair_scalp_analysis?.scalp?.[scalpKey]?.overall || null;
    return compareList.every(
        (product) => product.hair_scalp_analysis?.scalp?.[scalpKey]?.overall === firstOverall
    );
}

function allHaveSameSkinType(compareList, scalpKey) {
    if (compareList.length < 2) return true;
    const firstOverall = compareList[0].skin_analysis?.[scalpKey]?.overall || null;
    return compareList.every(
        (product) => product.skin_analysis?.[scalpKey]?.overall === firstOverall
    );
}
function collectAllIngredients(compareList) {
    const all = new Set();
    compareList.forEach((p) => {
        (p.ingredients || []).forEach((i) => all.add(i));
    });
    return [...all];
}

function allHaveSameIngredient(compareList, ing) {
    if (compareList.length < 2) return true;
    const firstHas = compareList[0].ingredients?.includes(ing);
    return compareList.every((p) => p.ingredients?.includes(ing) === firstHas);
}

export default function ComparePage() {
    const { compareList } = useCompare();
    const [showIngredients, setShowIngredients] = useState({});
    const [expandedIngredient, setExpandedIngredient] = useState({});
    const [showDifferencesOnly, setShowDifferencesOnly] = useState(false);

    const navigate = useNavigate();

    function toggleIngredients(barcode) {
        setShowIngredients((prev) => ({
            ...prev,
            [barcode]: !prev[barcode],
        }));
        setExpandedIngredient((prev) => ({ ...prev, [barcode]: null }));
    }

    function toggleIngredientDetails(barcode, ingredient) {
        setExpandedIngredient((prev) => {
            const current = prev[barcode];
            if (current === ingredient) return { ...prev, [barcode]: null };
            return { ...prev, [barcode]: ingredient };
        });
    }

    function getStars(rating) {
        if (!rating) return "☆☆☆☆☆";
        const fullStar = "★";
        const emptyStar = "☆";
        const rounded = Math.round(rating);
        let stars = "";
        for (let i = 1; i <= 5; i++) {
            stars += i <= rounded ? fullStar : emptyStar;
        }
        return stars;
    }

    const allEffects = collectAllEffects(compareList);
    const allRisks = collectAllRisks(compareList);
    const allHairTypes = collectAllHairTypes(compareList);
    const allScalpTypes = collectAllScalpTypes(compareList);
    const allIngredients = collectAllIngredients(compareList);

    const firstProductCategory = compareList.length > 0
        ? Object.keys(products).find((key) =>
            products[key].some((p) => p.barcode === compareList[0].barcode)
        )
        : null;

    const isSkin = firstProductCategory ? isSkinCategory(firstProductCategory) : false;

    return (
        <div className="cmp-scroll-container">
            <div className="cmp-compare-page">

                <header className="pre-header">
                    <h1 className="pre-title">💧 Sana İyi Gelecek 🧴</h1>
                    <label className="cmp-switch">
                        <input
                            type="checkbox"
                            checked={showDifferencesOnly}
                            onChange={(e) => setShowDifferencesOnly(e.target.checked)}
                        />
                        <span className="cmp-slider round"></span>
                    </label>
                    <span className="pre-subtitle">  Sadece Farklılıkları Göster</span>
                </header>
                <div className="cmp-grid">
                    <div className="cmp-row">
                        {compareList.map((product) => {
                            const category = Object.keys(products).find((key) =>
                                products[key].some((p) => p.barcode === product.barcode)
                            ); return (
                                <div
                                    key={product.barcode}
                                    className="cmp-card-header cmp-clickable-product"
                                    onClick={() => navigate(`/product/${product.barcode}`)}
                                >
                                    <div className="cmp-product-img-wrapper">
                                        <img src={getProductImage(category, product.barcode, product.id)} alt={product.name} className="cmp-product-image" />
                                        <div className="score-circle" style={getScoreStyle(product.total_score)}>
                                            <span className="score-big">{product.total_score}</span>
                                            <span className="score-small">/100</span>
                                        </div>
                                    </div>
                                    <h3 className="cmp-product-name">{product.name}</h3>
                                    <p className="cmp-product-brand">{product.brand}</p>
                                    <div className="cmp-rating-stars">
                                        <span className="cmp-stars-text">
                                            {product.star_rating
                                                ? getStars(parseFloat(product.star_rating))
                                                : "☆☆☆☆☆"}
                                        </span>
                                        <span className="cmp-star-value">
                                            {product.star_rating
                                                ? `(${parseFloat(product.star_rating).toFixed(1)}/5)`
                                                : "(0/5)"}
                                        </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className="cmp-row-label">🔬 İçerik</div>
                    <div className="cmp-row">
                        {compareList.map((product) => (
                            <div key={product.barcode} className="cmp-cell">
                                <div className="cmp-flags-grid">
                                    {ALL_FLAGS.map((flag) => {
                                        const productHas = !!product.flags?.[flag]?.length;
                                        if (!product.flags) return null;
                                        const isAllSame = allHaveSameFlag(compareList, flag);
                                        let styleClass = "";
                                        if (showDifferencesOnly) {
                                            styleClass = isAllSame ? "cmp-same-dim" : "cmp-highlight-diff";
                                        }
                                        return (
                                            <div key={flag} className={`cmp-flag-item ${styleClass}`}>
                                                {productHas ? (
                                                    <FontAwesomeIcon icon={faTimesCircle} className="cmp-flag-cross" />
                                                ) : (
                                                    <FontAwesomeIcon icon={faCheckCircle} className="cmp-flag-check" />
                                                )}
                                                <span>{FLAG_TRANSLATIONS[flag]}</span>
                                            </div>

                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="cmp-row-label">✨ Öne Çıkan Etkiler</div>
                    <div className="cmp-row">
                        {compareList.map((product) => {
                            const category = Object.keys(products).find((key) =>
                                products[key].some((p) => p.barcode === product.barcode)
                            );

                            const isSkin = isSkinCategory(category);
                            const effectsList = isSkin ? skinNotableEffects : notableEffects;

                            return (
                                <div key={product.barcode} className="cmp-cell">
                                    <div className="cmp-effects-list">
                                        {effectsList.map((effectObj) => {
                                            const effectKey = effectObj.key;
                                            const productHas = !!product.notable_effects?.[effectKey];
                                            if (!productHas) return null;

                                            const isAllSame = allHaveSameEffect(compareList, effectKey);
                                            let styleClass = "";
                                            if (showDifferencesOnly) {
                                                styleClass = isAllSame ? "cmp-same-dim" : "cmp-highlight-diff";
                                            }

                                            return (
                                                <div key={effectKey} className={`cmp-effect-item ${styleClass}`}>
                                                    <FontAwesomeIcon icon={faCheckCircle} className="cmp-effect-check" />
                                                    {effectObj.label}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="cmp-row-label">⚠️ Riskler</div>
                    <div className="cmp-row">
                        {compareList.map((product) => (
                            <div key={product.barcode} className="cmp-cell">
                                {allRisks.map((riskKey) => {
                                    const productHas = !!product.chemical_risks?.[riskKey];
                                    if (!productHas) return null;
                                    const isAllSame = allHaveSameRisk(compareList, riskKey);
                                    let styleClass = "";
                                    if (showDifferencesOnly) {
                                        styleClass = isAllSame ? "cmp-same-dim" : "cmp-highlight-diff";
                                    }
                                    return (
                                        <div key={riskKey} className={`cmp-risk-item ${styleClass}`}>
                                            <FontAwesomeIcon icon={faTimesCircle} className="cmp-risk-cross" />
                                            {categoryTitles[riskKey]}
                                        </div>
                                    );
                                })}
                                {!Object.keys(product.chemical_risks || {}).length && (
                                    <span className="cmp-none">Yok</span>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="cmp-row-label">{isSkin ? "🧴 Cilt Tipi" : "💇‍♀️ Saç Tipi"}</div>
                    <div className="cmp-row">
                        {compareList.map((product) => (
                            <div key={product.barcode} className="cmp-cell">
                                <div className="cmp-hair-scalp-list">
                                    {(isSkin ? Object.keys(product.skin_analysis || {}) : Object.keys(product.hair_scalp_analysis?.hair || {}))
                                        .map((key) => {
                                            const data = isSkin
                                                ? product.skin_analysis?.[key]
                                                : product.hair_scalp_analysis?.hair?.[key];

                                            if (!data) return null;
                                            const { overall } = data;
                                            const typeNames = isSkin ? skinTypeNames : hairTypeNames;
                                            const status = OVERALL_TO_STATUS[overall] || "unknown";
                                            const icon = getStatusIcon(status);
                                            const label = typeNames[key] || key;
                                            const isAllSame = isSkin
                                                ? allHaveSameSkinType(compareList, key)
                                                : allHaveSameHairType(compareList, key);
                                            let styleClass = showDifferencesOnly && isAllSame ? "cmp-same-dim" : "cmp-highlight-diff";

                                            return (
                                                <div key={key} className={`cmp-unique-card ${status} ${styleClass}`}>
                                                    <div className="cmp-unique-icon-label">
                                                        <span className="cmp-unique-icon">{icon}</span>
                                                        <span className="cmp-unique-label">{label}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {!isSkin && <><div className="cmp-row-label">🧴 Saç Derisi</div>
                        <div className="cmp-row">
                            {compareList.map((product) => (
                                <div key={product.barcode} className="cmp-cell">
                                    <div className="cmp-hair-scalp-list">
                                        {allScalpTypes.map((scalpKey) => {
                                            const scalpData = product.hair_scalp_analysis?.scalp?.[scalpKey];
                                            if (!scalpData) return null;

                                            const { overall } = scalpData;
                                            const status = OVERALL_TO_STATUS[overall] || "unknown";
                                            const icon = getStatusIcon(status);
                                            const label = scalpTypeNames[scalpKey] || scalpKey;
                                            const isAllSame = allHaveSameScalpType(compareList, scalpKey);

                                            let styleClass = "";
                                            if (showDifferencesOnly) {
                                                styleClass = isAllSame ? "cmp-same-dim" : "cmp-highlight-diff";
                                            }

                                            return (
                                                <div
                                                    key={scalpKey}
                                                    className={`cmp-unique-card ${status} ${styleClass}`}
                                                >
                                                    <div className="cmp-unique-icon-label">
                                                        <span className="cmp-unique-icon">{icon}</span>
                                                        <span className="cmp-unique-label">{label}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div></>}

                    <div className="cmp-row-label">🧪 İçindekiler</div>
                    <div className="cmp-row cmp-ingredients-row">
                        {compareList.map((product) => (
                            <div key={product.barcode} className="cmp-cell">
                                <button
                                    className="cmp-ingredients-toggle"
                                    onClick={() => toggleIngredients(product.barcode)}
                                >
                                    İçindekileri {showIngredients[product.barcode] ? "Gizle" : "Göster"}
                                </button>
                                {showIngredients[product.barcode] && (
                                    <div className="cmp-ingredient-list">
                                        {allIngredients.map((ing, idx) => {
                                            const productHas = product.ingredients?.includes(ing);
                                            if (!productHas) return null;
                                            const details = INGREDIENTS_EXPLANATIONS[ing] || {};
                                            const safetyClass = getSafetyClass(details.score);
                                            const isExpanded = expandedIngredient[product.barcode] === ing;
                                            const isAllSame = allHaveSameIngredient(compareList, ing);
                                            let styleClass = "";
                                            if (showDifferencesOnly) {
                                                styleClass = isAllSame ? "cmp-same-dim" : "cmp-highlight-diff";
                                            }
                                            return (
                                                <div
                                                    key={idx}
                                                    className={`cmp-ingredient-card ${isExpanded ? "expanded" : ""}`}
                                                >
                                                    <div
                                                        className={`cmp-ingredient-row ${safetyClass} ${styleClass}`}
                                                        onClick={() => toggleIngredientDetails(product.barcode, ing)}
                                                    >
                                                        <span className="ingredient-name">
                                                            {ing}
                                                            {details.localized_name ? ` (${details.localized_name})` : ""}
                                                        </span>
                                                        <span className="expand-indicator">
                                                            {isExpanded ? "▲" : "▼"}
                                                        </span>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="cmp-ingredient-details">
                                                            <p>{details.description || "Detay yok."}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
